import {
  Alert,
  Button,
  Checkbox,
  Form,
  type FormInstance,
  Input,
  InputNumber,
  Popover,
  Select,
  type SelectProps,
  Skeleton,
  Space,
  Switch,
  Tooltip,
} from "antd"
import { type DocumentReference, doc } from "firebase/firestore"
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DatabaseIcon,
  EditIcon,
  GlobeIcon,
  PlusIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { useDocumentData } from "react-firebase-hooks/firestore"

import LinearLogo from "../../assets/img/linear_logo.svg"
import { LINEAR_PROVIDER } from "../../credentials/providerTypes"
import { makeConverter } from "../../dbUtils"
import { db } from "../../firebaseApp"
import {
  GROUP_SETTINGS_COLLECTION,
  type Group,
  type GroupSettings,
} from "../../groups/types"
import useErrorPopup from "../../hooks/useErrorPopup"
import useUserCredential from "../../hooks/useUserCredential"
import type { AnswerMetadata, SearchTool } from "../../types/answerer"
import { ANSWER_METADATA_COLLECTION } from "../../types/common"
import { randomId } from "../../utils"
import {
  type UpdateAdvancedSettingsRequest,
  updateAdvancedSettings,
} from "./api"

const SEARCH_TOOL_ICONS = {
  QUILT_INTERNAL: <DatabaseIcon size={20} />,
  PUBLIC_WEBSITE: <GlobeIcon size={20} />,
  LINEAR: <img src={LinearLogo} alt="Linear" className="h-5 w-5" />,
}

interface FormDataType {
  settings: NonNullable<UpdateAdvancedSettingsRequest["settings"]>
  answer_metadata: NonNullable<UpdateAdvancedSettingsRequest["answer_metadata"]>
}

interface SearchToolListProps {
  form: FormInstance<FormDataType>
  name: string[]
  label: string
  tooltip: React.ReactNode
  addButtonText: string
  options: SelectProps["options"]
}

const SearchToolList: React.FC<SearchToolListProps> = ({
  form,
  name,
  label,
  tooltip,
  addButtonText,
  options,
}) => {
  const [visibleTool, setVisibleTool] = useState<number | null>(null)
  // NOTE: useWatch seems to initialize to undefined.
  const searchTools =
    Form.useWatch<SearchTool[]>(name, form) ??
    // @ts-expect-error name has wrong type.
    (form.getFieldValue(name) as SearchTool[])

  // We mostly do not allow the user to close if the form doesn't validate,
  // so that they can't accidentally try to submit and not see a failure.
  // But sometimes we allow it so that the popover doesn't stay open in an annoying way.
  // TODO(mgraczyk): Open the popover on error when the user tries to submit.
  const tryChangeTool = async (toIndex: number | null) => {
    try {
      await form.validateFields()
      setVisibleTool(toIndex)
    } catch (error) {
      // pass
      void error
    }
  }

  return (
    <Form.List name={name}>
      {(fields, { add, move, remove }) => (
        <Form.Item label={label} tooltip={tooltip}>
          <div className="flex flex-col gap-2">
            {fields.map((field, index) => {
              const popoverContent = (
                <div className="flex min-w-[400px] flex-col p-2">
                  <Button
                    type="text"
                    className="absolute right-2 top-2"
                    icon={<XIcon size={18} />}
                    onClick={() => setVisibleTool(null)}
                  />
                  <Form.Item
                    label="Name"
                    name={[field.name, "name"]}
                    rules={[
                      {
                        required: true,
                        message: "Please enter a tool name",
                      },
                    ]}
                  >
                    <Input placeholder="Tool name" />
                  </Form.Item>

                  <Form.Item
                    label="Data Source"
                    name={[field.name, "kind"]}
                    rules={[
                      {
                        required: true,
                        message: "Please select a data source",
                      },
                    ]}
                    tooltip={
                      <>
                        Determines which data source will be searched.
                        <br />
                        <ul className="pl-3">
                          <li>
                            <b>Quilt Internal Search:</b> Data synced by Quilt
                          </li>
                          <li>
                            <b>Public Website Search:</b> Data from public
                            websites that match the given URL patterns.
                          </li>
                          <li>
                            <b>Linear Search:</b> Issues from your Linear
                            workspace
                          </li>
                        </ul>
                      </>
                    }
                  >
                    <Select
                      options={options}
                      placeholder="Select data source"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Description"
                    name={[field.name, "description"]}
                    rules={[
                      {
                        required: true,
                        message: "Please enter a description",
                      },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="Some explanation for the model, when to use, what it searches..."
                      autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                  </Form.Item>

                  {searchTools?.[field.name]?.kind === "QUILT_INTERNAL" && (
                    <Form.Item
                      label="Tags Filter"
                      className="hidden"
                      name={[field.name, "tags_filter"]}
                    >
                      <Input.TextArea
                        placeholder="Tags filter..."
                        autoSize={{ minRows: 2, maxRows: 4 }}
                      />
                    </Form.Item>
                  )}

                  {searchTools?.[field.name]?.kind === "PUBLIC_WEBSITE" && (
                    <Form.Item
                      label="URL Patterns"
                      name={[field.name, "url_patterns"]}
                      tooltip={
                        <>
                          The tool will search all URLs that begin with any of
                          these patterns.
                          <br />
                          <br />
                          You can also include glob patterns like
                          example.com/*/article/* to match more complicated
                          <br />
                          <br />
                          This tool works when the patterns are as narrow as
                          possible.
                        </>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please enter at least one URL pattern",
                        },
                      ]}
                    >
                      <Select
                        mode="tags"
                        placeholder="Enter URL patterns"
                        tokenSeparators={[",", " ", "\n"]}
                      />
                    </Form.Item>
                  )}

                  {searchTools?.[field.name]?.kind === "LINEAR" && (
                    <Form.Item
                      label="Only include issues updated in the last X days"
                      name={[field.name, "max_days_old"]}
                      rules={[{ type: "number", min: 0 }]}
                      tooltip="Only include issues updated within the last X days (optional)"
                    >
                      <InputNumber
                        min={0}
                        style={{ width: "100%" }}
                        placeholder="e.g. 30"
                      />
                    </Form.Item>
                  )}

                  <div>
                    <Form.Item className="mb-0" label="Use by default in" />
                    <div className="flex flex-col gap-1">
                      <Form.Item
                        name={[field.name, "default_chat"]}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>Chat</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={[field.name, "default_questionnaire"]}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>Questionnaires</Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              )

              return (
                <Popover
                  key={field.key}
                  title="Edit Tool"
                  trigger="click"
                  open={visibleTool === index}
                  placement="top"
                  onOpenChange={(open) => {
                    if (open) {
                      return
                    }
                    setVisibleTool(null)
                  }}
                  content={popoverContent}
                >
                  <div
                    key={field.key}
                    className="group flex  min-h-14 cursor-pointer items-center justify-between rounded-md border border-gray-200 px-4 py-2"
                    aria-label="Edit tool"
                    onClick={async (e) => {
                      e.stopPropagation()
                      await tryChangeTool(index)
                    }}
                  >
                    <Form.Item name={[field.name, "oid"]} className="hidden">
                      <Input />
                    </Form.Item>

                    <div className="flex items-center gap-2">
                      {SEARCH_TOOL_ICONS[searchTools[field.name].kind]}
                      <div className="text-lg font-semibold">
                        {searchTools[field.name].name}
                      </div>
                    </div>

                    <div className="ml-4 hidden gap-1 group-hover:flex group-active:flex">
                      <Button
                        type="text"
                        icon={<EditIcon size={18} />}
                        title="Edit tool"
                        aria-label="Edit tool"
                      />
                      <Button
                        type="text"
                        icon={<ArrowUpIcon size={18} />}
                        onClick={(e) => {
                          e.stopPropagation()
                          move(index, index - 1)
                        }}
                        disabled={index === 0}
                        title="Move up"
                        aria-label="Move up"
                      />
                      <Button
                        type="text"
                        icon={<ArrowDownIcon size={18} />}
                        onClick={(e) => {
                          e.stopPropagation()
                          move(index, index + 1)
                        }}
                        disabled={index === fields.length - 1}
                        title="Move down"
                        aria-label="Move down"
                      />
                      <Button
                        type="text"
                        danger
                        icon={<Trash2Icon size={18} />}
                        onClick={(e) => {
                          e.stopPropagation()
                          remove(field.name)
                        }}
                        title="Remove tool"
                        aria-label="Remove tool"
                      />
                    </div>
                  </div>
                </Popover>
              )
            })}
          </div>
          <Button
            type="dashed"
            className="mt-2"
            onClick={async () => {
              add({
                oid: randomId(),
                name: "New Tool",
                description: "",
                kind: "QUILT_INTERNAL",
                default_chat: false,
                default_questionnaire: false,
              })
              await tryChangeTool(fields.length)
            }}
            icon={<PlusIcon />}
          >
            {addButtonText}
          </Button>
        </Form.Item>
      )}
    </Form.List>
  )
}

interface Props {
  group: Group
}

const settingsDocRef = (groupOid: string): DocumentReference<GroupSettings> =>
  doc(db, GROUP_SETTINGS_COLLECTION, groupOid).withConverter(
    makeConverter<GroupSettings>(),
  )

const metadataDocRef = (
  metadataOid: string,
): DocumentReference<AnswerMetadata> =>
  doc(db, ANSWER_METADATA_COLLECTION, metadataOid).withConverter(
    makeConverter<AnswerMetadata>(),
  )

const SettingsTab: React.FC<Props> = ({ group }) => {
  const [form] = Form.useForm<FormDataType>()
  const [submitting, setSubmitting] = useState<boolean>(false)
  const { handleSuccess, handleError } = useErrorPopup()
  const [settings, settingsLoading, settingsError] = useDocumentData(
    settingsDocRef(group.oid),
  )
  const [answerMetadata, metadataLoading, metadataError] = useDocumentData(
    metadataDocRef(group.oid),
  )
  const [dirty, setDirty] = useState<boolean>(false)
  const { userCredentials, loading: userCredentialLoading } =
    useUserCredential()
  const credentialsByProvider = Object.fromEntries(
    userCredentials.map((credential) => [credential.provider, credential]),
  )
  const hasLinearCredential = !!credentialsByProvider[LINEAR_PROVIDER]

  const toolKindOptions = useMemo(() => {
    const baseOptions = [
      {
        value: "QUILT_INTERNAL",
        label: "Quilt Internal Search",
        tooltip: "Search within Quilt's internal knowledge base and documents",
      },
      {
        value: "PUBLIC_WEBSITE",
        label: "Public Website Search",
        tooltip: "Search public websites based on specified URL patterns",
      },
    ]
    if (hasLinearCredential) {
      baseOptions.push({
        value: "LINEAR",
        label: "Linear Search",
        tooltip: "Search issues from Linear",
      })
    }
    return baseOptions
  }, [hasLinearCredential])

  console.log("toolKindOptions", toolKindOptions)

  const onFinish = useCallback(
    async (values: FormDataType) => {
      setSubmitting(true)
      try {
        await updateAdvancedSettings(group.oid, {
          settings: values.settings,
          answer_metadata: values.answer_metadata,
        })

        handleSuccess("Updated settings")
      } catch (error) {
        handleError({ error, prefix: "Couldn't update settings" })
      } finally {
        setSubmitting(false)
      }
    },
    [group.oid, handleSuccess, handleError],
  )

  const loading = settingsLoading ?? metadataLoading ?? userCredentialLoading
  const error = settingsError ?? metadataError
  if (error) {
    return (
      <Alert
        message="Could not load settings"
        showIcon
        description={error.message}
        type="error"
      />
    )
  }

  if (loading) {
    return (
      <>
        <h3>Advanced Settings</h3>
        <Skeleton active loading />
      </>
    )
  }

  const initialValues = {
    settings,
    answer_metadata: answerMetadata,
  }

  return (
    <Form
      form={form}
      layout="vertical"
      className="w-full max-w-3xl"
      disabled={loading || submitting}
      initialValues={initialValues}
      onFinish={onFinish}
      onValuesChange={() => setDirty(true)}
    >
      <h3>Advanced Settings</h3>
      <Form.Item
        label="Automatically import questionnaire on completion"
        tooltip="When a Questionnaire is marked as complete in the Questionnaire Assistant, Quilt will automatically import the responses from that questionnaire to the Answer Bank."
        name={["settings", "import_questionnaire_on_complete"]}
        valuePropName="checked"
        className=""
      >
        <Switch />
      </Form.Item>
      <Form.Item
        label="Company Name"
        name={["answer_metadata", "company_name"]}
        tooltip="The name of your company as you would like the model to write it."
      >
        <Input placeholder="Example Company, Inc..." />
      </Form.Item>
      <Form.Item
        label="Global Custom Prompt"
        name={["answer_metadata", "global_custom_prompt"]}
        tooltip={`This prompt will be used everywhere for your group.
                    Use it to add important information or context, or control the style and voice.`}
      >
        <Input.TextArea
          placeholder="Our products include..."
          autoSize={{ minRows: 3, maxRows: 5 }}
        />
      </Form.Item>

      <SearchToolList
        form={form}
        name={["answer_metadata", "search_tools"]}
        label="Search Tools"
        tooltip={
          <>
            Search tools are used by Quilt to search your data when answering
            questions.
            <br />
            You can configure each tool and specify whether it should be used by
            default in chat and questionnaires.
          </>
        }
        addButtonText="Add Search Tool"
        options={toolKindOptions}
      />

      <Form.Item>
        <Space>
          <Tooltip
            title={
              dirty
                ? "Save changes to the settings."
                : "Nothing has changed. Modify settings to save."
            }
          >
            <Button type="primary" htmlType="submit" disabled={!dirty}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Tooltip>
          <Button
            htmlType="button"
            onClick={() => {
              form.resetFields()
              setDirty(false)
            }}
          >
            Reset
          </Button>
        </Space>
      </Form.Item>
    </Form>
  )
}

export default SettingsTab
